import * as React from 'react'
import kebabCase from 'lodash/kebabCase'
import {format} from 'date-fns'
import {mmap} from '@freckle/maybe'
import {H1} from '@frontrow/console/src/components/typography'
import {Button} from '@frontrow/console/src/components/button'

import {Link} from '@frontrow/console/src/components/ui/link'
import Restricted from '@frontrow/console/src/components/common/restricted'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faSignInAlt, faList, faTrashRestore} from '@fortawesome/free-solid-svg-icons'
import {type ColumnDef} from '@tanstack/react-table'

import {NumberFilter} from '@frontrow/console/src/components/common/data-table/filters'
import DataTable, {
  type FilterT,
  type FetchDataParamsT,
  type DataTableColumnMeta
} from '@frontrow/console/src/components/common/data-table'
import ErrorAlert from '@frontrow/console/src/components/common/error-alert'
import TeacherInformationButtonModal from '@frontrow/console/src/components/pages/teachers-page/teacher-information-button-modal'
import DeleteTeacherButtonModal from '@frontrow/console/src/components/pages/teachers-page/delete-teacher-button-modal'
import {type TeacherWithAffiliationT} from '@frontrow/console/src/models/teacher'
import {Routes} from '@frontrow/console/src/routers/routes'

type Props = {
  defaultFiltered: Array<FilterT>
  teacherData: Array<TeacherWithAffiliationT>
  fetchData: (fetchDataParams: FetchDataParamsT) => void
  isLoading: boolean
  error: string | undefined | null
  impersonateTeacher: (teacherEmail: string) => void
  deleteTeacher: (teacherId: number) => void
  undeleteTeacher: (teacherId: number) => void
}

function dataTestId(id: string): DataTableColumnMeta {
  return {dataTest: `${kebabCase(id)}-search-input`}
}

// Two of the four icons come from child components, so the hit target and hover
// state are applied from the cell rather than each button.
const ACTION_CELL =
  'flex items-center gap-0.5 [&_a]:rounded [&_a]:p-1.5 [&_button]:rounded' +
  ' [&_button]:p-1.5 [&_a:hover]:bg-surface-hover [&_button:hover]:bg-surface-hover'

export default function TeachersPage(props: Props): React.ReactElement {
  const {
    fetchData,
    teacherData,
    isLoading,
    error,
    defaultFiltered,
    impersonateTeacher,
    deleteTeacher,
    undeleteTeacher
  } = props

  const columns: ColumnDef<TeacherWithAffiliationT, unknown>[] = [
    {
      id: 'teacher.id',
      header: 'ID',
      accessorFn: d => d.teacher.id,
      meta: {Filter: NumberFilter, ...dataTestId('teacher.id')} satisfies DataTableColumnMeta
    },
    {
      id: 'teacher.firstName',
      header: 'First Name',
      accessorFn: d => d.teacher.firstName,
      cell: ctx => strikeThroughIfDeleted(ctx.row.original, t => t.teacher.firstName),
      meta: dataTestId('teacher.firstName')
    },
    {
      id: 'teacher.lastName',
      header: 'Last Name',
      accessorFn: d => d.teacher.lastName,
      cell: ctx => strikeThroughIfDeleted(ctx.row.original, t => t.teacher.lastName),
      meta: dataTestId('teacher.lastName')
    },
    {
      id: 'teacher.email',
      header: 'Email',
      accessorFn: d => d.teacher.email,
      cell: ctx => strikeThroughIfDeleted(ctx.row.original, t => t.teacher.email),
      meta: dataTestId('teacher.email')
    },
    {
      id: 'teacher.deletedAt',
      header: 'Deleted',
      accessorFn: d => mmap(dt => format(dt, 'MMM d, yyyy'), d.teacher.deletedAt),
      meta: dataTestId('teacher.deletedAt')
    },
    {
      id: 'school.id',
      header: 'School ID',
      accessorFn: d => mmap(s => s.id, d.school),
      meta: {Filter: NumberFilter, ...dataTestId('school.id')} satisfies DataTableColumnMeta
    },
    {
      id: 'school.name',
      header: 'School',
      accessorFn: d => mmap(s => s.name, d.school),
      meta: dataTestId('school.name')
    },
    {
      id: 'district.id',
      header: 'District ID',
      accessorFn: d => mmap(dt => dt.id, d.district),
      meta: {Filter: NumberFilter, ...dataTestId('district.id')} satisfies DataTableColumnMeta
    },
    {
      id: 'district.name',
      header: 'District',
      accessorFn: d => mmap(dt => dt.name, d.district),
      meta: dataTestId('district.name')
    },
    {
      id: 'action',
      header: 'Action',
      accessorFn: () => null,
      cell: ctx => {
        const teacherData = ctx.row.original
        const deleteUndeleteButton =
          teacherData.teacher.deletedAt === null || teacherData.teacher.deletedAt === undefined ? (
            <DeleteTeacherButtonModal teacher={teacherData.teacher} deleteTeacher={deleteTeacher} />
          ) : (
            <Button
              style="link"
              title="Undelete Teacher"
              dataTest="undelete-teacher-button"
              onClick={() => {
                undeleteTeacher(teacherData.teacher.id)
              }}
            >
              <FontAwesomeIcon icon={faTrashRestore} size="lg" />
            </Button>
          )

        return (
          <div className={ACTION_CELL}>
            <Restricted to={['admin', 'support']}>
              <Button
                style="link"
                title="Impersonate Teacher"
                dataTest="impersonate-teacher-button"
                onClick={() => {
                  impersonateTeacher(teacherData.teacher.email)
                }}
              >
                <FontAwesomeIcon icon={faSignInAlt} size="lg" />
              </Button>
            </Restricted>
            <Link
              title="Update Teacher Licenses"
              dataTest="update-teacher-licenses-button"
              href={Routes.teachers.teacherLicenses(teacherData.teacher.id)}
              state={{filters: defaultFiltered}}
              reactRouter
            >
              <FontAwesomeIcon icon={faList} size="lg" />
            </Link>
            <TeacherInformationButtonModal teacherData={teacherData} />
            {deleteUndeleteButton}
          </div>
        )
      },
      enableColumnFilter: false,
      enableSorting: false
    }
  ]

  const errorNode = error !== null && error !== undefined ? <ErrorAlert>{error}</ErrorAlert> : null
  return (
    <div>
      <H1>Teachers</H1>
      {errorNode}
      <DataTable
        columns={columns}
        fetchData={fetchData}
        data={teacherData}
        isLoading={isLoading}
        filterable
        manualFiltering
        updateUrlOnFilter
        defaultPageSize={100}
        defaultFiltered={defaultFiltered}
      />
    </div>
  )
}

function strikeThroughIfDeleted(
  teacherData: TeacherWithAffiliationT,
  access: (a: TeacherWithAffiliationT) => React.ReactNode
): React.ReactElement {
  const deletedAt = teacherData.teacher.deletedAt
  if (deletedAt === null || deletedAt === undefined) {
    return <React.Fragment>{access(teacherData)}</React.Fragment>
  }

  return (
    <React.Fragment>
      <s className="text-muted-foreground">{access(teacherData)}</s>
    </React.Fragment>
  )
}
